import { cx } from "@emotion/css";
import React, { CSSProperties, forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { getStaticSpriteUrl } from "../constants";
import { getIconSpriteId, IncFaIconName } from "./IncFaIconsList";
import { IncFaIconProps } from "./types";

// Sprite cache to avoid re-fetching
const spriteCache = new Map<string, Promise<void>>();
const injectedSprites = new Set<string>();

const injectSvgSprite = (spriteUrl: string, variant?: string): Promise<void> => {
  // Extract the sprite file path (without the hash)
  const [spritePath] = spriteUrl.split("#");

  // If already injected, skip
  if (injectedSprites.has(spritePath)) {
    return Promise.resolve();
  }

  // If currently fetching, wait for existing promise
  const existingPromise = spriteCache.get(spritePath);
  if (existingPromise) {
    return existingPromise;
  }

  // Create new fetch promise
  const fetchPromise = (async () => {
    try {
      const response = await fetch(spritePath);
      const svgContent = await response.text();

      // Create container if it doesn't exist
      let container = document.getElementById("fa-svg-sprite-container");
      if (!container) {
        container = document.createElement("div");
        container.id = "fa-svg-sprite-container";
        container.style.display = "none";
        container.setAttribute("aria-hidden", "true");
        document.body.insertBefore(container, document.body.firstChild);
      }

      // Parse and inject the SVG content
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
      const svgElement = svgDoc.documentElement;

      // Append all children from the sprite SVG, making IDs variant-specific to avoid conflicts
      const containerElement = document.getElementById("fa-svg-sprite-container");
      if (containerElement && variant) {
        Array.from(svgElement.children).forEach(child => {
          const clonedChild = child.cloneNode(true) as Element;
          // Make IDs variant-specific to avoid conflicts between solid and regular sprites
          if (clonedChild.id) {
            clonedChild.id = `${clonedChild.id}-${variant}`;
          }
          // Also update any internal references to IDs
          const allElements = clonedChild.querySelectorAll("[id]");
          allElements.forEach(el => {
            if (el.id) {
              el.id = `${el.id}-${variant}`;
            }
          });
          containerElement.appendChild(clonedChild);
        });
      } else if (containerElement) {
        // Fallback: append without modification if no variant provided
        Array.from(svgElement.children).forEach(child => {
          containerElement.appendChild(child.cloneNode(true));
        });
      }

      injectedSprites.add(spritePath);
    } catch (error) {
      console.error("Failed to load SVG sprite:", spritePath, error);
      spriteCache.delete(spritePath); // Allow retry on next attempt
    }
  })();

  spriteCache.set(spritePath, fetchPromise);
  return fetchPromise;
};

export const IncFaIcon = forwardRef<HTMLDivElement, IncFaIconProps>((props, ref) => {
  const {
    iconName = "image-landscape",
    className: pClassName = "",
    variant = "solid",
    stackIconName,
    color,
    spin = false,
    spinPulse = false,
    spinReverse = false,
    pulse = false,
    beat = false,
    fade = false,
    beatFade = false,
    bounce = false,
    shake = false,
    flip = null,
    size = null,
    pull = null,
    rotation = null,
    regular = false,
    style: pStyle,
    ...restProps
  } = props;

  const [spritesLoaded, setSpritesLoaded] = useState(false);

  const style: CSSProperties = useMemo(
    () =>
      color
        ? {
            ...(pStyle || {}),
            color
          }
        : pStyle || {},
    [color, pStyle]
  );

  const computedVariant = regular ? "regular" : variant;

  const className = useMemo(
    () =>
      cx("fa svg-inline--fa", {
        [pClassName]: Boolean(pClassName),
        "fa-spin": spin,
        "fa-spin-pulse": spinPulse,
        "fa-spin-reverse": spinReverse,
        "fa-pulse": pulse,
        "fa-beat": beat,
        "fa-fade": fade,
        "fa-beat-fade": beatFade,
        "fa-bounce": bounce,
        "fa-shake": shake,
        [`fa-flip-${flip}`]: Boolean(flip),
        [`fa-${size}`]: typeof size === "string" && Boolean(size),
        [`fa-pull-${pull}`]: Boolean(pull),
        [`fa-rotate-${rotation}`]: Boolean(rotation)
      }),
    [beat, beatFade, bounce, fade, flip, pClassName, pull, pulse, rotation, shake, size, spin, spinPulse, spinReverse]
  );

  const getSvgUrlAndViewBox = useCallback(
    (iconName: IncFaIconName) => {
      iconName = (iconName.startsWith("fa-") ? iconName.replace("fa-", "") : iconName) as IncFaIconName;
      const { iconSpriteId, iconVariants, viewBox } = getIconSpriteId(iconName);
      const finalVariant = iconVariants.includes(computedVariant) ? computedVariant : iconVariants[0];
      const spritePath = getStaticSpriteUrl(`fa-icon-sprite/${finalVariant}.svg`);

      return {
        spriteUrl: `${spritePath}#${iconSpriteId}`,
        spriteId: `#${iconSpriteId}-${finalVariant}`,
        viewBox,
        variant: finalVariant
      };
    },
    [computedVariant]
  );

  // Load sprites on mount
  useEffect(() => {
    const loadSprites = async () => {
      const { spriteUrl, variant } = getSvgUrlAndViewBox(iconName);
      const promises = [injectSvgSprite(spriteUrl, variant)];

      if (stackIconName) {
        const { spriteUrl: stackSpriteUrl, variant: stackVariant } = getSvgUrlAndViewBox(stackIconName);
        promises.push(injectSvgSprite(stackSpriteUrl, stackVariant));
      }

      await Promise.all(promises);
      setSpritesLoaded(true);
    };

    loadSprites();
  }, [getSvgUrlAndViewBox, iconName, stackIconName]);

  const baseIcon = useMemo(() => {
    if (!spritesLoaded) {
      return null;
    }

    const { spriteId, viewBox } = getSvgUrlAndViewBox(iconName);
    return (
      <svg
        {...restProps}
        className={className}
        fill={color || "currentColor"}
        stroke={color || "currentColor"}
        style={style}
        viewBox={viewBox}
      >
        <use xlinkHref={spriteId} />
      </svg>
    );
  }, [spritesLoaded, className, color, getSvgUrlAndViewBox, iconName, restProps, style]);

  const stackIcon = useMemo(() => {
    if (!stackIconName || !spritesLoaded) {
      return null;
    }

    const { spriteId, viewBox } = getSvgUrlAndViewBox(stackIconName);
    const style: CSSProperties = {
      ...(pStyle || {}),
      position: "absolute",
      left: -1,
      top: 2
    };

    return (
      <svg
        {...restProps}
        className={className}
        fill={color || "currentColor"}
        stroke={color || "currentColor"}
        style={style}
        viewBox={viewBox}
      >
        <use xlinkHref={spriteId} />
      </svg>
    );
  }, [spritesLoaded, className, color, getSvgUrlAndViewBox, pStyle, restProps, stackIconName]);

  // Show nothing or a placeholder while loading
  if (!spritesLoaded) {
    return null; // Or return a loading placeholder if you prefer
  }

  if (!stackIconName) {
    return baseIcon;
  }

  return (
    <div
      className="fa-layers display-inline-block position-relative"
      ref={ref}
    >
      {baseIcon}
      {stackIcon}
    </div>
  );
});
